"use client";

import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";

export default function BeemPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1c1c1f);

    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(10, 10, 15);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    const orbit = new OrbitControls(camera, renderer.domElement);
    orbit.enableDamping = true;
    orbit.dampingFactor = 0.05;

    const transformControls = new TransformControls(camera, renderer.domElement);
    scene.add(transformControls);
    transformControls.addEventListener("dragging-changed", (e: any) => { orbit.enabled = !e.value; });

    const gridHelper = new THREE.GridHelper(50, 50, 0x44444c, 0x2c2c35);
    scene.add(gridHelper);
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    const vertexShaderCode = `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `;
    const shaderPresets: Record<string, string> = {
      neon: `
        uniform vec3 uColor; uniform float uTime; uniform float uSpeed; uniform float uGlow; uniform float uRepeat; varying vec2 vUv;
        void main() {
          float centerDist = abs(vUv.y - 0.5) * 2.0;
          float glow = pow(1.0 - centerDist, 3.0) * uGlow;
          float pattern = sin(vUv.x * uRepeat - (uTime * uSpeed)) * 0.5 + 0.5;
          gl_FragColor = vec4(uColor * glow * (0.6 + pattern * 0.4), (1.0 - centerDist));
        }
      `,
      plasma: `
        uniform vec3 uColor; uniform float uTime; uniform float uSpeed; uniform float uGlow; uniform float uRepeat; varying vec2 vUv;
        float noise(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
        void main() {
          vec2 uv = vUv * vec2(uRepeat, 1.0);
          float timeOffset = uTime * uSpeed;
          float line = abs(vUv.y - 0.5 + sin(uv.x - timeOffset) * 0.15 * noise(vec2(floor(uv.x), uTime * 0.05)));
          float intensity = pow(0.04 / line, 1.2) * uGlow;
          gl_FragColor = vec4(uColor * intensity, 1.0 - (line * 2.0));
        }
      `,
      rainbow: `
        uniform float uTime; uniform float uSpeed; uniform float uGlow; uniform float uRepeat; varying vec2 vUv;
        void main() {
          float centerDist = abs(vUv.y - 0.5) * 2.0;
          float xPos = vUv.x * uRepeat - (uTime * uSpeed);
          vec3 color = vec3(sin(xPos) * 0.5 + 0.5, sin(xPos + 2.0) * 0.5 + 0.5, sin(xPos + 4.0) * 0.5 + 0.5);
          float glow = pow(1.0 - centerDist, 2.5) * uGlow;
          gl_FragColor = vec4(color * glow, 1.0 - centerDist);
        }
      `
    };

    // Data
    type BeamData = {
      attachment0: string | null; attachment1: string | null;
      renderMode: string; shaderPreset: string; color: string;
      width: number; offsetY: number; speed: number; repeat: number; glow: number;
      mesh: THREE.Mesh | null;
      loadedTextureMap?: THREE.Texture;
      uniforms: Record<string, { value: any }>;
    };
    type VirtualNode = {
      id: string; name: string; type: string; parent: string | null;
      children: string[]; threeObj: any;
    };

    const virtualRegistry: Record<string, VirtualNode> = {
      root: { id: "root", name: "Workspace", type: "Workspace", parent: null, children: [], threeObj: null }
    };
    let activeNodeId: string | null = null;
    let objectCounter = 0;
    const beamsList: string[] = [];

    function createInstance(type: string, parentId: string): string {
      objectCounter++;
      const id = "inst_" + objectCounter;
      const name = type + objectCounter;
      let threeObj: any = null;

      if (type === "Part") {
        const geo = new THREE.BoxGeometry(2, 2, 2);
        const mat = new THREE.MeshStandardMaterial({ color: 0xa3a2a5, roughness: 0.4 });
        threeObj = new THREE.Mesh(geo, mat);
        threeObj.position.set((Math.random() - 0.5) * 6, 1, (Math.random() - 0.5) * 6);
        scene.add(threeObj);
        threeObj.userData.instanceId = id;
      } else if (type === "Attachment") {
        const geo = new THREE.SphereGeometry(0.25, 16, 16);
        const mat = new THREE.MeshBasicMaterial({ color: 0xffaa00, wireframe: true, depthTest: false });
        threeObj = new THREE.Mesh(geo, mat);
        threeObj.renderOrder = 999;
        const parentNode = virtualRegistry[parentId];
        if (parentNode && parentNode.threeObj && parentNode.type === "Part") {
          parentNode.threeObj.add(threeObj);
          threeObj.position.set(0, 1.1, 0);
        } else {
          threeObj.position.set((Math.random() - 0.5) * 4, 2, (Math.random() - 0.5) * 4);
          scene.add(threeObj);
        }
        threeObj.userData.instanceId = id;
      } else if (type === "Beam") {
        threeObj = {
          attachment0: null, attachment1: null,
          renderMode: "shader", shaderPreset: "neon", color: "#00ffcc",
          width: 1.0, offsetY: 0.0, speed: 2.0, repeat: 5.0, glow: 1.5, mesh: null,
          uniforms: {
            uTime: { value: 0 }, uColor: { value: new THREE.Color(0x00ffcc) },
            uSpeed: { value: 2.0 }, uGlow: { value: 1.5 }, uRepeat: { value: 5.0 }
          }
        } as BeamData;
      }

      virtualRegistry[id] = { id, name, type, parent: parentId, children: [], threeObj };
      if (parentId && virtualRegistry[parentId]) {
        virtualRegistry[parentId].children.push(id);
      }
      if (type === "Beam") beamsList.push(id);
      rebuildExplorerDOM();
      selectExplorerNode(id);
      return id;
    }

    function rebuildExplorerDOM() {
      const treeContainer = document.getElementById("tree-root-children");
      if (!treeContainer) return;
      treeContainer.innerHTML = "";
      function parseChildren(nodeId: string, el: HTMLElement) {
        virtualRegistry[nodeId].children.forEach(childId => {
          const child = virtualRegistry[childId];
          let icon = "📁";
          if (child.type === "Part") icon = "📦";
          if (child.type === "Attachment") icon = "📍";
          if (child.type === "Beam") icon = "⚡";
          if (child.type === "Model") icon = "🧱";
          const nodeRow = document.createElement("div");
          nodeRow.className = "tree-node";
          const label = document.createElement("div");
          label.className = "tree-label" + (activeNodeId === childId ? " tree-selected" : "");
          label.innerHTML = `<span class="icon">${icon}</span> <span id="lbl-${childId}">${child.name}</span>`;
          label.onclick = (e) => { e.stopPropagation(); selectExplorerNode(childId); };
          nodeRow.appendChild(label);
          const cc = document.createElement("div");
          nodeRow.appendChild(cc);
          el.appendChild(nodeRow);
          parseChildren(childId, cc);
        });
      }
      parseChildren("root", treeContainer);
    }

    function updatePropertyFields(obj: THREE.Object3D) {
      const px = document.getElementById("pPosX") as HTMLInputElement;
      const py = document.getElementById("pPosY") as HTMLInputElement;
      const pz = document.getElementById("pPosZ") as HTMLInputElement;
      const rx = document.getElementById("pRotX") as HTMLInputElement;
      const ry = document.getElementById("pRotY") as HTMLInputElement;
      const rz = document.getElementById("pRotZ") as HTMLInputElement;
      if (px) px.value = obj.position.x.toFixed(2);
      if (py) py.value = obj.position.y.toFixed(2);
      if (pz) pz.value = obj.position.z.toFixed(2);
      if (rx) rx.value = String(Math.round(THREE.MathUtils.radToDeg(obj.rotation.x)));
      if (ry) ry.value = String(Math.round(THREE.MathUtils.radToDeg(obj.rotation.y)));
      if (rz) rz.value = String(Math.round(THREE.MathUtils.radToDeg(obj.rotation.z)));
    }

    function syncBeamFields(bData: BeamData) {
      (document.getElementById("pBeamColor") as HTMLInputElement).value = bData.color;
      (document.getElementById("pBeamWidth") as HTMLInputElement).value = String(bData.width);
      (document.getElementById("pBeamOffset") as HTMLInputElement).value = String(bData.offsetY);
      (document.getElementById("pBeamSpeed") as HTMLInputElement).value = String(bData.speed);
      (document.getElementById("pBeamRepeat") as HTMLInputElement).value = String(bData.repeat);
      (document.getElementById("pBeamGlow") as HTMLInputElement).value = String(bData.glow);
      (document.getElementById("vBeamWidth") as HTMLElement).innerText = String(bData.width);
      (document.getElementById("vBeamOffset") as HTMLElement).innerText = String(bData.offsetY);
      (document.getElementById("vBeamSpeed") as HTMLElement).innerText = String(bData.speed);
      (document.getElementById("vBeamRepeat") as HTMLElement).innerText = String(bData.repeat);
      (document.getElementById("vBeamGlow") as HTMLElement).innerText = String(bData.glow);
      (document.getElementById("beamShaderSettings") as HTMLElement).style.display = bData.renderMode === "shader" ? "block" : "none";
      (document.getElementById("beamTextureSettings") as HTMLElement).style.display = bData.renderMode === "texture" ? "block" : "none";
      (document.getElementById("btnAssignAttachment0") as HTMLElement).innerText = bData.attachment0 ? "A0: " + virtualRegistry[bData.attachment0].name : "Set Attachment0 (Start)";
      (document.getElementById("btnAssignAttachment1") as HTMLElement).innerText = bData.attachment1 ? "A1: " + virtualRegistry[bData.attachment1].name : "Set Attachment1 (End)";
    }

    function selectExplorerNode(id: string) {
      activeNodeId = id;
      document.querySelectorAll(".tree-label").forEach(el => el.classList.remove("tree-selected"));
      const rootLbl = document.getElementById("node-root");
      if (id === "root" && rootLbl) rootLbl.classList.add("tree-selected");
      const node = virtualRegistry[id];
      const propContainer = document.getElementById("property-editor-container") as HTMLElement;
      const noSel = document.getElementById("no-selection-msg") as HTMLElement;
      if (!node || id === "root") {
        propContainer.style.display = "none"; noSel.style.display = "block";
        transformControls.detach(); return;
      }
      propContainer.style.display = "block"; noSel.style.display = "none";
      (document.getElementById("pInstanceName") as HTMLInputElement).value = node.name;
      (document.getElementById("propSectionTransform") as HTMLElement).style.display = (node.threeObj && node.type !== "Beam") ? "block" : "none";
      (document.getElementById("propSectionPart") as HTMLElement).style.display = node.type === "Part" ? "block" : "none";
      (document.getElementById("propSectionBeam") as HTMLElement).style.display = node.type === "Beam" ? "block" : "none";
      if (node.threeObj && node.type !== "Beam") { transformControls.attach(node.threeObj); updatePropertyFields(node.threeObj); }
      else transformControls.detach();
      if (node.type === "Beam") syncBeamFields(node.threeObj as BeamData);
      rebuildExplorerDOM();
    }

    function processBeams(elapsed: number) {
      beamsList.forEach(beamId => {
        const bNode = virtualRegistry[beamId];
        if (!bNode) return;
        const bData = bNode.threeObj as BeamData;
        if (!bData.attachment0 || !bData.attachment1) return;
        const a0Node = virtualRegistry[bData.attachment0];
        const a1Node = virtualRegistry[bData.attachment1];
        if (!a0Node || !a1Node) return;
        const pA = new THREE.Vector3(), pB = new THREE.Vector3();
        a0Node.threeObj.getWorldPosition(pA);
        a1Node.threeObj.getWorldPosition(pB);
        const dirA = new THREE.Vector3(0, bData.offsetY, 2).applyQuaternion(a0Node.threeObj.quaternion);
        const dirB = new THREE.Vector3(0, bData.offsetY, -2).applyQuaternion(a1Node.threeObj.quaternion);
        const ctrlA = new THREE.Vector3().addVectors(pA, dirA);
        const ctrlB = new THREE.Vector3().addVectors(pB, dirB);
        const curve = new THREE.CatmullRomCurve3([pA, ctrlA, ctrlB, pB]);
        if (bData.mesh) { scene.remove(bData.mesh); (bData.mesh.geometry as THREE.BufferGeometry).dispose(); }
        const tubeGeo = new THREE.TubeGeometry(curve, 64, bData.width * 0.15, 8, false);
        let mat: THREE.Material;
        if (bData.renderMode === "shader") {
          bData.uniforms.uTime.value = elapsed;
          mat = new THREE.ShaderMaterial({
            vertexShader: vertexShaderCode,
            fragmentShader: shaderPresets[bData.shaderPreset],
            uniforms: bData.uniforms,
            transparent: true, blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide, depthWrite: false
          });
        } else {
          mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(bData.color), transparent: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false });
          if (bData.loadedTextureMap) {
            (mat as THREE.MeshBasicMaterial).map = bData.loadedTextureMap;
            bData.loadedTextureMap.repeat.set(bData.repeat, 1);
            bData.loadedTextureMap.offset.x = elapsed * (bData.speed * 0.1);
          }
        }
        bData.mesh = new THREE.Mesh(tubeGeo, mat);
        scene.add(bData.mesh);
      });
    }

    // Init demo
    const p1 = createInstance("Part", "root");
    const a1 = createInstance("Attachment", p1);
    virtualRegistry[a1].name = "StartAttachment";
    const p2 = createInstance("Part", "root");
    virtualRegistry[p2].threeObj.position.set(8, 3, -5);
    const a2 = createInstance("Attachment", p2);
    virtualRegistry[a2].name = "EndAttachment";
    const beamId = createInstance("Beam", "root");
    virtualRegistry[beamId].threeObj.attachment0 = a1;
    virtualRegistry[beamId].threeObj.attachment1 = a2;
    selectExplorerNode(beamId);

    const runtimeClock = new THREE.Clock();
    let rafId: number;
    function loop() {
      rafId = requestAnimationFrame(loop);
      const elapsed = runtimeClock.getElapsedTime();
      processBeams(elapsed);
      orbit.update();
      renderer.render(scene, camera);
    }
    loop();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    // Wire up DOM inputs after mount
    const insertBtn = document.getElementById("btnInsertObj");
    if (insertBtn) {
      insertBtn.addEventListener("click", () => {
        const type = (document.getElementById("creationType") as HTMLSelectElement).value;
        let parentId = "root";
        if (activeNodeId) {
          const sel = virtualRegistry[activeNodeId];
          if (["Folder", "Model", "Part"].includes(sel.type)) parentId = activeNodeId;
          else parentId = sel.parent || "root";
        }
        createInstance(type, parentId);
      });
    }
    const toolMove = document.getElementById("toolMove");
    const toolRotate = document.getElementById("toolRotate");
    if (toolMove) toolMove.addEventListener("click", () => { transformControls.setMode("translate"); });
    if (toolRotate) toolRotate.addEventListener("click", () => { transformControls.setMode("rotate"); });

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    window.addEventListener("pointerdown", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".panel") || target.tagName === "BUTTON" || target.tagName === "INPUT" || target.tagName === "SELECT") return;
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const pool: THREE.Object3D[] = [];
      Object.values(virtualRegistry).forEach(n => { if (n.threeObj && n.type !== "Beam") pool.push(n.threeObj); });
      const hits = raycaster.intersectObjects(pool, true);
      if (hits.length > 0) {
        let rootObj = hits[0].object;
        while (rootObj.parent && !rootObj.userData.instanceId && rootObj.parent.type !== "Scene") rootObj = rootObj.parent;
        if (rootObj.userData.instanceId) selectExplorerNode(rootObj.userData.instanceId);
      } else if (!(transformControls as any).dragging) selectExplorerNode("root");
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: "#222", color: "#e0e0e0", fontFamily: "'Segoe UI', Arial, sans-serif", userSelect: "none" }}>
      <style>{`
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
        .panel{position:absolute;background:#2d2d2d;border:1px solid #3c3c3c;padding:10px;box-shadow:0 4px 20px rgba(0,0,0,0.5);max-height:92vh;overflow-y:auto;width:320px;font-size:13px;}
        #left-panel{top:10px;left:10px;}
        #right-panel{top:10px;right:10px;}
        h3{margin-top:0;border-bottom:1px solid #3c3c3c;padding-bottom:6px;color:#fff;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;}
        .section-title{font-size:11px;text-transform:uppercase;color:#8aa4c3;margin:12px 0 6px 0;font-weight:bold;}
        .control-group{margin-bottom:8px;}
        .row-inputs{display:flex;gap:5px;margin-bottom:5px;}
        .row-inputs label{font-size:10px;color:#aaa;width:15px;text-align:center;align-self:center;}
        label{display:block;font-size:12px;margin-bottom:3px;color:#b5b5b5;}
        .label-val{float:right;color:#a6e22e;font-weight:bold;}
        input[type="number"],select,input[type="text"]{background:#202020;border:1px solid #3a3a3a;color:#fff;padding:4px 6px;border-radius:3px;font-size:12px;width:100%;box-sizing:border-box;}
        input[type="range"]{width:100%;accent-color:#007acc;background:#444;height:4px;cursor:pointer;}
        .btn{background:#3a3a3a;color:#fff;border:1px solid #555;padding:6px;border-radius:3px;cursor:pointer;font-size:12px;width:100%;transition:background 0.1s;margin-bottom:4px;box-sizing:border-box;text-align:left;}
        .btn:hover{background:#4a4a4a;border-color:#007acc;}
        .btn-active{background:#007acc !important;border-color:#0098ff !important;}
        .color-wrapper{display:flex;align-items:center;gap:8px;}
        input[type="color"]{border:none;width:40px;height:22px;cursor:pointer;background:none;padding:0;}
        .switch-group{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
        input[type="checkbox"]{width:15px;height:15px;accent-color:#007acc;cursor:pointer;}
        .tree-node{margin-left:12px;padding-left:6px;border-left:1px dotted #444;}
        .tree-label{padding:3px 5px;cursor:pointer;border-radius:2px;display:flex;align-items:center;gap:4px;}
        .tree-label:hover{background:#353535;}
        .tree-selected{background:#1e4b75 !important;color:#fff;}
        .icon{font-weight:bold;font-size:11px;width:14px;display:inline-block;}
        #property-editor-container{border-top:1px solid #444;margin-top:10px;padding-top:10px;}
      `}</style>

      <div ref={containerRef} id="canvas-container" style={{ width: "100vw", height: "100vh" }} />

      <div className="panel" id="left-panel">
        <h3>Explorer (Hierarchy)</h3>
        <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
          <select id="creationType" style={{ flex: 1 }}>
            <option value="Folder">Folder</option>
            <option value="Model">Model</option>
            <option value="Part">Part (Mesh)</option>
            <option value="Attachment">Attachment</option>
            <option value="Beam">Beam Link</option>
          </select>
          <button className="btn" id="btnInsertObj" style={{ width: "auto", marginBottom: 0, padding: "4px 10px" }}>Insert</button>
        </div>
        <div id="explorer-tree">
          <div className="tree-label tree-selected" id="node-root">
            <span className="icon">🌍</span> DataModel (Roblox)
          </div>
          <div id="tree-root-children" className="tree-node" />
        </div>
        <div className="section-title">Gizmo Tool Mode</div>
        <div style={{ display: "flex", gap: "5px" }}>
          <button className="btn btn-active" id="toolMove" style={{ textAlign: "center", flex: 1 }}>Move</button>
          <button className="btn" id="toolRotate" style={{ textAlign: "center", flex: 1 }}>Rotate</button>
        </div>
      </div>

      <div className="panel" id="right-panel">
        <h3>Properties Window</h3>
        <div id="no-selection-msg" style={{ color: "#888", fontStyle: "italic" }}>Select an object from Explorer to edit its properties.</div>
        <div id="property-editor-container" style={{ display: "none" }}>
          <div className="control-group">
            <label>Instance Name</label>
            <input type="text" id="pInstanceName" />
          </div>
          <div id="propSectionTransform">
            <div className="section-title">CFrame / Transform</div>
            <div className="control-group">
              <label>Position</label>
              <div className="row-inputs">
                <label>X</label><input type="number" id="pPosX" step={0.1} />
                <label>Y</label><input type="number" id="pPosY" step={0.1} />
                <label>Z</label><input type="number" id="pPosZ" step={0.1} />
              </div>
            </div>
            <div className="control-group">
              <label>Orientation (Deg)</label>
              <div className="row-inputs">
                <label>X</label><input type="number" id="pRotX" step={5} />
                <label>Y</label><input type="number" id="pRotY" step={5} />
                <label>Z</label><input type="number" id="pRotZ" step={5} />
              </div>
            </div>
          </div>
          <div id="propSectionPart" style={{ display: "none" }}>
            <div className="section-title">Part Properties</div>
            <div className="control-group">
              <label>BrickColor</label>
              <div className="color-wrapper"><input type="color" id="pPartColor" defaultValue="#ffffff" /></div>
            </div>
          </div>
          <div id="propSectionBeam" style={{ display: "none" }}>
            <div className="section-title">Beam Links Configuration</div>
            <button className="btn" id="btnAssignAttachment0">Set Attachment0 (Start)</button>
            <button className="btn" id="btnAssignAttachment1">Set Attachment1 (End)</button>
            <div className="section-title">Beam Render Style</div>
            <div className="control-group">
              <label>TextureMode</label>
              <select id="beamRenderMode">
                <option value="shader">Procedural Custom Shader</option>
                <option value="texture">External Image Texture</option>
              </select>
            </div>
            <div id="beamShaderSettings">
              <div className="control-group">
                <label>Shader Preset</label>
                <select id="beamShaderPreset">
                  <option value="neon">Neon Glow Laser</option>
                  <option value="plasma">Plasma Lightning Storm</option>
                  <option value="rainbow">Rainbow RGB Wave</option>
                </select>
              </div>
            </div>
            <div id="beamTextureSettings" style={{ display: "none" }}>
              <div className="control-group">
                <label>Upload Texture File (PNG/JPG)</label>
                <input type="file" id="beamTextureFile" accept="image/*" />
              </div>
            </div>
            <div className="section-title">Beam Appearance</div>
            <div className="control-group">
              <label>Color Tint</label>
              <div className="color-wrapper"><input type="color" id="pBeamColor" defaultValue="#00ffcc" /></div>
            </div>
            <div className="control-group">
              <label>Width <span className="label-val" id="vBeamWidth">1.0</span></label>
              <input type="range" id="pBeamWidth" min={0.1} max={4.0} step={0.1} defaultValue={1.0} />
            </div>
            <div className="control-group">
              <label>Curve Offset Y <span className="label-val" id="vBeamOffset">0.0</span></label>
              <input type="range" id="pBeamOffset" min={-5.0} max={5.0} step={0.1} defaultValue={0.0} />
            </div>
            <div className="control-group">
              <label>Texture Speed <span className="label-val" id="vBeamSpeed">2.0</span></label>
              <input type="range" id="pBeamSpeed" min={0.0} max={10.0} step={0.1} defaultValue={2.0} />
            </div>
            <div className="control-group">
              <label>Texture Repeat <span className="label-val" id="vBeamRepeat">5.0</span></label>
              <input type="range" id="pBeamRepeat" min={1.0} max={25.0} step={1.0} defaultValue={5.0} />
            </div>
            <div className="control-group">
              <label>Glow Intensity <span className="label-val" id="vBeamGlow">1.5</span></label>
              <input type="range" id="pBeamGlow" min={0.1} max={5.0} step={0.1} defaultValue={1.5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
